/**
 * AMD-52 — the Supply Drop. Deterministic ichor purchases, priced against the Graft.
 *
 * The tension is borrowed from kimi-test-matt's intermission store (score spent now vs
 * banked for the final tally); the currency is not. That build invented a score wallet —
 * this spine says "no score-chasing" — so here the wallet is the one currency the run
 * already ratifies: Abyssal Ichor (AMD-35). Every kit bought is a Graft pull pushed
 * further away, and prices are *fractions of the current pull cost*, so the escalating
 * §9.12.3 cost curve keeps the trade honest all run.
 *
 * Pure sim: no RNG (a purchase is certainty — that is its whole pitch against the gacha),
 * no DOM. The pause panel is the v1 surface; it calls `buySupply` through the integrator.
 */

import { ichorBalance, pullCost, spendIchor } from './gacha';
import type { World } from './world';

export interface SupplyItemDef {
  id: string;
  name: string;
  /** One-line effect summary for the card. */
  effect: string;
  /** Price as a fraction of the current Graft pull cost, floored, min 1. */
  costFraction: number;
  available(w: World): boolean;
  apply(w: World): void;
}

/** HP the repair kit restores (clamped at max). */
export const REPAIR_HP = 25;
/** Max-hull (and hull) the plating kit grants. */
export const PLATING_HP = 10;

export const SUPPLY_ITEMS: readonly SupplyItemDef[] = [
  {
    id: 'supply.repair',
    name: 'Repair Kit',
    effect: `+${REPAIR_HP} HP`,
    costFraction: 0.6,
    available: (w) => w.player.hp < w.player.max_hp,
    apply: (w) => {
      const p = w.player;
      p.hp = Math.min(p.max_hp, p.hp + REPAIR_HP);
    },
  },
  {
    id: 'supply.plating',
    name: 'Hull Plating',
    effect: `+${PLATING_HP} max HP`,
    costFraction: 0.9,
    available: () => true,
    apply: (w) => {
      const p = w.player;
      p.max_hp += PLATING_HP;
      p.hp += PLATING_HP;
    },
  },
];

export function supplyCost(w: World, item: SupplyItemDef): number {
  const c = Math.floor(pullCost(w) * item.costFraction);
  return c < 1 ? 1 : c;
}

export function supplyById(id: string): SupplyItemDef | null {
  for (let i = 0; i < SUPPLY_ITEMS.length; i++) if (SUPPLY_ITEMS[i].id === id) return SUPPLY_ITEMS[i];
  return null;
}

/** Affordable and applicable right now. The run may be paused — the pause panel is the store. */
export function canBuySupply(w: World, id: string): boolean {
  const item = supplyById(id);
  if (item === null || w.outcome !== 'running') return false;
  if (!item.available(w)) return false;
  return ichorBalance(w) >= supplyCost(w, item);
}

/** Debit then apply, atomically from the sim's point of view. False = nothing changed. */
export function buySupply(w: World, id: string): boolean {
  const item = supplyById(id);
  if (item === null || w.outcome !== 'running') return false;
  if (!item.available(w)) return false;
  if (!spendIchor(w, supplyCost(w, item))) return false;
  item.apply(w);
  return true;
}
